import React, { useEffect, useRef, useState } from "react";
import Styles from "./inputDropdown.module.scss";
import ArrowDown from "../svg/arrowDown";
import { IdsGenerator } from "../../utils/idGenerator";
import { SECONDARYCOLOR } from "../colors";

function optionLabel(option: any): string {
  if (option == null) return "";
  if (typeof option === "object") return String(option.label ?? "");
  return String(option);
}

function InputDropdown(props: any) {
  const {
    labelname,
    hasError,
    optionsArray,
    click,
    defaultValue = "",
    no_shadow = "false",
    isValid = null,
    default_value = "",
    disabled = false,
    resetValue,
    customInputPlaceHolder,
    className,
    id,
    borderRadius = "false",
    isMandatory = false,
    style,
  } = props;

  const [selectedOption, setSelectedOption] = useState<any>();
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const dropDownRef: any = useRef(null);

  useEffect(() => {
    setSelectedOption(default_value);
  }, [default_value]);

  useEffect(() => {
    if (resetValue !== undefined) {
      setSelectedOption("");
    }
  }, [resetValue]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = () => !disabled && setIsDropdownOpen(!isDropdownOpen);

  const handleOptionClick = (option: any) => {
    setSelectedOption(option);
    click(option);
    setIsDropdownOpen(false);
  };

  const selectedLabel = optionLabel(selectedOption);
  const displayLabel = selectedLabel || optionLabel(customInputPlaceHolder);

  const disabledState = (
    <div
      className={`${
        hasError !== ""
          ? Styles.dropdownError
          : isValid
            ? Styles.validDropdown
            : Styles.dropdown
      } ${no_shadow === "true" ? "shadow-none" : ""} d-flex align-items-center`}
      style={{ cursor: "not-allowed", opacity: 0.5, ...style }}
    >
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <p
          className={`mb-0 ${Styles.selectedLabel}`}
          title={defaultValue || undefined}
        >
          {defaultValue ? (
            defaultValue
          ) : (
            <span
              style={{
                color: SECONDARYCOLOR,
              }}
            >
              Select Option
            </span>
          )}
        </p>
      </div>
      <div className="ms-auto">
        <div>
          <ArrowDown color={SECONDARYCOLOR} />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {disabled ? (
        disabledState
      ) : (
        <div className={Styles.dropdownWrapper} ref={dropDownRef} style={style}>
          {labelname && (
            <p className={`${Styles.formLabel} m-0 pb-2`}>
              {labelname}
              {isMandatory && <span className={Styles.mandatoryField}> *</span>}
            </p>
          )}

          <div
            id={id}
            data-testid={id}
            className={`${Styles.dropdownButton} ${className || ""}`}
            style={{
              borderRadius: borderRadius === "true" ? "8px" : "",
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.5 : 1,
            }}
            onClick={toggleDropdown}
          >
            <span
              className={Styles.selectedLabel}
              title={displayLabel || props.placeholder || undefined}
            >
              {displayLabel || (
                <span style={{ color: SECONDARYCOLOR }}>
                  {props.placeholder || "Select Option"}
                </span>
              )}
            </span>
            <span className={Styles.dropdownArrow}>
              <ArrowDown
                color={SECONDARYCOLOR}
                style={{
                  transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              />
            </span>
          </div>

          {isDropdownOpen && (
            <div className={Styles.dropdownMenu}>
              {(optionsArray || []).length > 0 ? (
                (optionsArray || []).map((option: any, index: number) => (
                  <div
                    key={`Dropdown_Array_${index}`}
                    id={IdsGenerator({
                      prefix: id,
                      id: "dropdown option",
                      suffix: option?.label || option,
                    })}
                    data-testid={IdsGenerator({
                      prefix: id,
                      id: "dropdown option",
                      suffix: option?.label || option,
                    })}
                    className={`${Styles.dropdownItem} ${
                      selectedOption === option ? Styles.active : ""
                    }`}
                    onClick={() => handleOptionClick(option)}
                    role="option"
                    aria-selected={selectedOption === option}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOptionClick(option);
                      }
                    }}
                  >
                    <span className={Styles.optionLabel} title={optionLabel(option)}>
                      {optionLabel(option)}
                    </span>
                  </div>
                ))
              ) : (
                <div className={Styles.dropdownItem}>No options found</div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default InputDropdown;